import React from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { ContentCard } from '../components/ContentCard';
import { ShieldCheckIcon, HeartPulseIcon, CarIcon, WrenchIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
const coverageTypes = [{
  icon: HeartPulseIcon,
  title: 'Medical Coverage (PIP)',
  description: 'Minimum $20,000 required to ride without a helmet. We recommend $100,000+ for adequate protection. Medical bills add up fast after an accident.',
  variant: 'important' as const
}, {
  icon: CarIcon,
  title: 'Uninsured/Underinsured Motorist',
  description: "Essential coverage. Many drivers have minimal insurance or none at all. This protects you when they can't cover your damages.",
  variant: 'warning' as const
}, {
  icon: ShieldCheckIcon,
  title: 'Collision Coverage',
  description: 'Covers damage to your bike from accidents, regardless of fault. Important if your motorcycle has significant value.',
  variant: 'default' as const
}, {
  icon: WrenchIcon,
  title: 'Accessory/Custom Parts',
  description: "Standard policies often don't cover aftermarket parts. If you've customized your bike, get additional coverage for those investments.",
  variant: 'default' as const
}];
export function Insurance() {
  return <div className="min-h-screen bg-[#0a0a0a] safe-bottom" data-id="element-265">
      <PageHeader title="Recommended Insurance" subtitle="Protect yourself and your bike" data-id="element-266" />

      <main className="px-4 py-6" data-id="element-267">
        {/* Minimum vs Recommended */}
        <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-268">
          <motion.div className="bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-2xl p-4" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} data-id="element-269">
            <p className="text-[#dc2626] text-xs font-medium mb-1" data-id="element-270">
              MI Minimum
            </p>
            <p className="text-white text-2xl font-bold" data-id="element-271">$20k</p>
            <p className="text-[#a3a3a3] text-xs" data-id="element-272">Medical (for no helmet)</p>
          </motion.div>

          <motion.div className="bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-2xl p-4" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-273">
            <p className="text-[#22c55e] text-xs font-medium mb-1" data-id="element-274">
              Recommended
            </p>
            <p className="text-white text-2xl font-bold" data-id="element-275">$100k+</p>
            <p className="text-[#a3a3a3] text-xs" data-id="element-276">Medical coverage</p>
          </motion.div>
        </div>

        {/* Coverage Types */}
        <motion.div className="space-y-3 mb-6" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.2
      }} data-id="element-277">
          {coverageTypes.map((coverage, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + index * 0.1
        }} data-id="element-278">
              <ContentCard icon={coverage.icon} title={coverage.title} description={coverage.description} variant={coverage.variant} data-id="element-279" />
            </motion.div>)}
        </motion.div>

        {/* Checklist */}
        <motion.div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-4 mb-6" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6
      }} data-id="element-280">
          <p className="text-white font-semibold mb-3" data-id="element-281">Coverage Checklist</p>
          <div className="space-y-2" data-id="element-282">
            {['Liability (required)', 'Personal Injury Protection (PIP)', 'Uninsured Motorist', 'Collision', 'Comprehensive', 'Accessory Coverage'].map((item, index) => <div key={index} className="flex items-center gap-3" data-id="element-283">
                <CheckCircleIcon className="w-4 h-4 text-[#d4a853]" data-id="element-284" />
                <span className="text-[#a3a3a3] text-sm" data-id="element-285">{item}</span>
              </div>)}
          </div>
        </motion.div>

        {/* Warning */}
        <motion.div className="bg-[#d4a853]/10 border border-[#d4a853]/30 rounded-2xl p-4 flex gap-3" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.7
      }} data-id="element-286">
          <AlertTriangleIcon className="w-6 h-6 text-[#d4a853] flex-shrink-0" data-id="element-287" />
          <div data-id="element-288">
            <p className="text-[#d4a853] font-semibold mb-1" data-id="element-289">
              Review Your Policy
            </p>
            <p className="text-[#a3a3a3] text-sm" data-id="element-290">
              Many riders are underinsured without knowing it. Contact us for a
              free policy review.
            </p>
          </div>
        </motion.div>
      </main>
    </div>;
}